import { useState } from "react";
import { Calendar, Clock, Zap, Timer } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { cn } from "../lib/utils";

/**
 * Scheduling step for batch sends.
 * Lets the user choose:
 *  - Send now / Schedule for later (date + time)
 *  - Interval between messages (seconds) — to avoid blocking
 *
 * Props:
 *  - value: { mode: "now"|"scheduled", scheduledAt: ISOString|null, intervalSeconds: number }
 *  - onChange: (next) => void
 */
export function ScheduleStep({ value, onChange }) {
  const mode = value?.mode || "now";
  const scheduledAt = value?.scheduledAt || "";
  const intervalSeconds = value?.intervalSeconds ?? 5;

  const update = (patch) => onChange?.({ ...value, ...patch });

  // Local datetime → "YYYY-MM-DDTHH:MM" for the input
  const minDateTime = (() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5); // earliest = 5min from now
    const pad = (n) => String(n).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  })();

  return (
    <div className="space-y-4" data-testid="schedule-step">
      {/* Mode toggle */}
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => update({ mode: "now" })}
          data-testid="schedule-mode-now"
          className={cn(
            "rounded-xl border-2 p-4 text-right transition-all flex flex-col items-end gap-1",
            mode === "now"
              ? "bg-brand-50 border-brand text-brand shadow-sm"
              : "bg-white border-stone-200 hover:border-stone-300 text-stone-600"
          )}
        >
          <div className="flex items-center gap-2 self-end">
            <span className="font-heading font-semibold">שליחה מיידית</span>
            <Zap className="h-4 w-4" />
          </div>
          <span className="text-xs">שלח עכשיו לכולם</span>
        </button>
        <button
          type="button"
          onClick={() => update({ mode: "scheduled" })}
          data-testid="schedule-mode-scheduled"
          className={cn(
            "rounded-xl border-2 p-4 text-right transition-all flex flex-col items-end gap-1",
            mode === "scheduled"
              ? "bg-purple-50 border-purple-600 text-purple-700 shadow-sm"
              : "bg-white border-stone-200 hover:border-stone-300 text-stone-600"
          )}
        >
          <div className="flex items-center gap-2 self-end">
            <span className="font-heading font-semibold">תזמון משלוח</span>
            <Calendar className="h-4 w-4" />
          </div>
          <span className="text-xs">בחר תאריך ושעה</span>
        </button>
      </div>

      {/* Date + Time picker */}
      {mode === "scheduled" && (
        <div className="rounded-xl border border-purple-200 bg-purple-50/40 p-4 space-y-3" data-testid="schedule-datetime">
          <Label className="flex items-center gap-2 font-semibold text-purple-900">
            <Clock className="h-4 w-4" />
            מתי לשלוח?
          </Label>
          <Input
            type="datetime-local"
            value={scheduledAt}
            min={minDateTime}
            onChange={(e) => update({ scheduledAt: e.target.value })}
            className="bg-white"
            data-testid="schedule-datetime-input"
          />
          <p className="text-xs text-purple-700">
            ההודעות יישלחו אוטומטית בזמן שתבחר.
          </p>
        </div>
      )}

      {/* Interval slider */}
      <div className="rounded-xl border border-stone-200 bg-white p-4 space-y-3" data-testid="schedule-interval">
        <div className="flex items-center justify-between">
          <Label className="flex items-center gap-2 font-semibold text-stone-800">
            <Timer className="h-4 w-4" />
            הפסקה בין הודעות
          </Label>
          <span className="text-sm font-semibold text-brand bg-brand-50 px-3 py-1 rounded-full">
            {intervalSeconds === 0 ? "ללא הפסקה" : `${intervalSeconds} שניות`}
          </span>
        </div>
        <input
          type="range"
          min="0"
          max="30"
          step="1"
          value={intervalSeconds}
          onChange={(e) => update({ intervalSeconds: parseInt(e.target.value, 10) })}
          className="w-full accent-brand cursor-pointer"
          data-testid="schedule-interval-slider"
        />
        <div className="flex justify-between text-[11px] text-stone-500">
          <span>30 שניות (איטי, בטוח יותר)</span>
          <span>15</span>
          <span>0 (מהיר)</span>
        </div>
        <p className="text-xs text-stone-600 leading-relaxed">
          💡 <span className="font-medium">המלצה:</span> 5-10 שניות בין הודעות מונע סימון כספאם ע"י WhatsApp.
        </p>
      </div>
    </div>
  );
}
